import { createService } from "services/serviceFactory";
import { ChangeNiasEntityRequest, EPSignUpRequest, SignInRequest, SignUpRequest } from "./request";
import { SignInResponse, TokensResponse } from "./response";
import { getAuthHeaders } from "modules/common";

const authService = createService("auth");

async function checkEpUserRole(epToken: string) {
    return authService.post("/auth/v1/Auth/CheckEpUserRole", undefined, {
        headers: {
            Authorization: `Bearer ${epToken}`,
        },
    });
}

async function signUp(data: SignUpRequest) {
    return authService.post("/auth/v1/Auth/RegisterCitizen", data);
}

async function signUpEp(data: EPSignUpRequest) {
    return authService.post(
        "/auth/v1/Auth/RegisterEp",
        { email: data.email, returnUrl: data.returnUrl, role: data.role },
        {
            headers: {
                Authorization: `Bearer ${data.token}`,
            },
        }
    );
}

async function logOut(accessToken: string) {
    return authService.post(
        "/auth/v1/Auth/Logout",
        {},
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            withCredentials: true,
        }
    );
}

async function isLocalLoginAllowed() {
    return await authService.get<boolean>("/auth/v1/Auth/IsLocalLoginAllowed");
}

async function signIn(data: SignInRequest) {
    return authService.post<SignInResponse>("/auth/v1/Auth/Login", data, { withCredentials: true });
}

async function signInEpToken(token: string, returnUrl?: string) {
    return authService.get<SignInResponse>("/auth/v1/Auth/Login-Ep", {
        params: { returnUrl },
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
}

async function signInAd() {
    return authService.get<SignInResponse>("/ad-auth/v1/Auth/Login-Ad", {
        withCredentials: true,
    });
}

async function checkSession(accessToken: string) {
    const resp = await authService.get("/auth/v1/Auth/CheckSession", {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
        validateStatus: () => true, //dont throw exception
    });
    return resp.status === 200;
}

async function renewToken(refreshToken: string) {
    return authService.post<TokensResponse>("/auth/v1/Auth/RefreshTokens", { refreshToken }, { withCredentials: true });
}

async function sendForgotPasswordEmail(email: string) {
    return authService.post("/auth/v1/Auth/SendForgotPasswordEmail", { email });
}

async function activateUserAccount(code: string) {
    return authService.post("/auth/v1/Auth/ActivateUserAccount", { code });
}

async function verifyResetPasswordCode(code: string) {
    return authService.post("/auth/v1/Auth/VerifyResetPasswordCode", { code });
}

async function resetPassword(code: string, password: string) {
    return authService.post("/auth/v1/Auth/ResetPassword", { code, password });
}

async function changeNiasEntity(request: ChangeNiasEntityRequest, accessToken: string) {
    const authHeaders = getAuthHeaders(accessToken);
    return authService.post<TokensResponse>("/auth/v1/Auth/ChangeEntity", request, {
        ...authHeaders,
        withCredentials: true,
    });
}

async function resetMobilePassword(
    currentPassword: string,
    newPassword: string,
    accessToken: string
) {
    const authHeaders = getAuthHeaders(accessToken);
    return authService.post("/auth/v1/Auth/ResetMobilePassword", { currentPassword, newPassword }, {
        ...authHeaders,
    });
}

export {
    checkEpUserRole,
    logOut,
    isLocalLoginAllowed,
    signIn,
    signUp,
    signUpEp,
    signInEpToken,
    signInAd,
    checkSession,
    renewToken,
    sendForgotPasswordEmail,
    activateUserAccount,
    verifyResetPasswordCode,
    resetPassword,
    changeNiasEntity,
    resetMobilePassword,
};
